import {css, Theme} from "@emotion/react";
import {Button} from "@pg-design/button";
import {flex, m, textAlign, w100} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text";

import {ContentImageValue, ContentItem, ContentItemType} from "../../types/Entry";
import {blockquoteContent, contentWrapper} from "./article_content_styles";
import {ArticleGallery} from "./ArticleGallery";
import {ContentEmbedIframe} from "./ContentEmbedIframe";
import {XEmbed} from "./XEmbed";

const normalizeImageValue = (value: string | ContentImageValue): ContentImageValue =>
    typeof value === "string" ? {title: "", description: "", html: value} : value;

export const ArticleContentItem = ({contentItem}: {contentItem: ContentItem}) => {
    switch (contentItem.type) {
        case ContentItemType.BLOCKQUOTE:
            return (
                <div css={contentWrapper}>
                    <blockquote>
                        {contentItem.value.header && (
                            <Text as="p" variant="headline_5">
                                {contentItem.value.header}
                            </Text>
                        )}
                        <div css={blockquoteContent} dangerouslySetInnerHTML={{__html: contentItem.value.text}} />
                        {contentItem.value.cite && <p css={textAlign("end")}>{contentItem.value.cite}</p>}
                    </blockquote>
                </div>
            );

        case ContentItemType.LINK:
            return (
                <div css={[contentWrapper, flex("center", "center"), w100]}>
                    <a
                        href={contentItem.value.url}
                        {...(contentItem.value.blank && {target: "_blank"})}
                        rel="noopener noreferrer"
                    >
                        <Button variant="filled_primary" title={contentItem.value.title}>
                            {contentItem.value.text}
                        </Button>
                    </a>
                </div>
            );

        case ContentItemType.VIMEO:
        case ContentItemType.YOUTUBE:
        case ContentItemType.SPOTIFY:
            return (
                <div css={contentWrapper}>
                    <ContentEmbedIframe type={contentItem.type} embedUrl={contentItem.value.embed_url} />
                </div>
            );

        case ContentItemType.X:
            return (
                <div css={contentWrapper}>
                    <XEmbed html={contentItem.value.html} />
                </div>
            );

        case ContentItemType.LINKEDIN:
            return (
                <div
                    css={[contentWrapper, flex("center", "center"), w100]}
                    dangerouslySetInnerHTML={{__html: contentItem.value.html}}
                />
            );

        case ContentItemType.TEXT:
            return <div css={contentWrapper} dangerouslySetInnerHTML={{__html: contentItem.value}} />;

        case ContentItemType.IMAGE: {
            const {html, description} = normalizeImageValue(contentItem.value);

            return (
                <div css={contentWrapper}>
                    <div dangerouslySetInnerHTML={{__html: html}} />

                    {description && (
                        <Text as="p" variant="info_txt_2" css={imageDescription}>
                            {description}
                        </Text>
                    )}
                </div>
            );
        }

        case ContentItemType.GALLERY:
            return <ArticleGallery images={contentItem.value} />;

        default:
            return null;
    }
};

/** Podwójny `&&` bije selektor `p` z contentWrapper, który dokłada duże marginesy akapitowe. */
const imageDescription = (theme: Theme) => css`
    && {
        ${m(1, 0, 0, 0)};
        color: ${theme.colors.gray[700]};
    }
`;
